/**
 * @file index.tsx
 * @author liuchen(loocliu@tencent.com)
 */
import { isEmpty } from 'lodash';
import React, { forwardRef, MouseEventHandler, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { RichMediaValue } from '@/interface/media';
import { useTypedSelector } from '@/store';
import { EventType, IIMMessage, SenderType } from '@/store/modules/__endpoints__/imMessage';
import { getVoice } from '@/store/modules/__endpoints__/tts';
import { isWoa } from '@/utils/getApiDomain';

import Loading from '../loading';
import Plot from './plot';
import Receive from './receive';
import Send from './send';
import styles from './style/index.module.less';

interface IMessagesProps {
  data: (IIMMessage & { isUp?: boolean })[];
  isLoading: boolean;
  visible: boolean;
  primary: string;
  onLoadBefore: () => void;
  onExpandMessage?: (id: string) => void;
  style?: React.CSSProperties;
  sceneId?: string;
  avatar?: RichMediaValue;
}

const Messages = forwardRef<{ play: (id: string) => void; clear: () => void }, IMessagesProps>((props, ref) => {
  const { data, isLoading, visible, primary, style, avatar, onExpandMessage, onLoadBefore } = props;

  const { vuid } = useTypedSelector(store => store.user);
  const lastEleRef = useRef<HTMLDivElement>(null);

  const location = useLocation();

  const [scale, setScale] = useState<{ id?: string; scale: number }>({ scale: 1.0 });
  const [loadingId, setLoadingId] = useState<string>();

  const audioRef = useRef<HTMLAudioElement>(null);
  const scaleTimerRef = useRef<NodeJS.Timeout>();
  const voiceMapRef = useRef<{ [key: string]: string }>({});

  const [voice, setVoice] = useState<string>();

  const handleScroll: MouseEventHandler<HTMLDivElement> = useCallback((e) => {
    if (e.currentTarget.scrollTop === 0) {
      onLoadBefore();
    }
  }, [onLoadBefore]);

  const handlePlay = useCallback(
    (id: string) => {
      (async () => {
        const msg = data.find(item => item.ID === id);
        if (!msg?.payload.data.isFinish || (!vuid && !isWoa)) {
          return;
        }
        setLoadingId(id);
        let voice = voiceMapRef.current[id];
        if (voice === undefined) {
          const res = await getVoice({
            botId: msg.from.replace('@RBT#', ''),
            userId: isWoa ? 'woa' : `${vuid}`,
            text: msg.payload.data.message?.text?.text || '',
          });
          setVoice(res);
          if (!isEmpty(res)) {
            voice = res;
            voiceMapRef.current[id] = voice;
          }
        }
        if (scaleTimerRef.current !== undefined) {
          clearInterval(scaleTimerRef.current);
          scaleTimerRef.current = undefined;
        }
        if (audioRef.current !== null) {
          audioRef.current.pause();
          setScale({ scale: 1.0 });
          setLoadingId(undefined);
          const audio = audioRef.current;
          audio.src = voice;
          audio.oncanplay = () => {
            let len = Math.ceil(audio.duration || 0);
            setScale({ id, scale: 0.8 });
            let isNarrow = false;
            const timer = setInterval(() => {
              setScale({ id, scale: isNarrow ? 0.8 : 1.0 });
              len = len - 1;
              isNarrow = !isNarrow;
              if (len === 0) {
                clearInterval(timer);
                setScale({ id, scale: 1.0 });
                isNarrow = false;
              }
            }, 1000);
            scaleTimerRef.current = timer;
            audio.play();
          };
          audio.onended = () => {
            setScale({ scale: 1.0 });
          };
        }
      })();
    },
    [data, vuid, isWoa],
  );

  const handleClear = useCallback(() => {
    audioRef.current?.pause();
    if (scaleTimerRef.current) {
      clearInterval(scaleTimerRef.current);
    }
    setScale({ scale: 1.0 });
    scaleTimerRef.current = undefined;
  }, []);

  useImperativeHandle(ref, () => ({ play: handlePlay, clear: handleClear }), [handlePlay, handleClear]);

  useEffect(() => () => {
    handleClear();
  }, [location]);

  useEffect(() => {
    if (lastEleRef.current) {
      lastEleRef.current.scrollIntoView();
    }
  }, [data[data.length - 1]?.payload?.data?.message?.text?.text]);

  return (
    <div
      className={styles.root}
      style={{ ...style, opacity: !visible ? 0.0 : 1.0 }}
      onScroll={handleScroll}
    >
      {data.map(({ ID, payload, flow, nick }) => (
        <div key={ID} >
          {flow === 'out' && payload.data.message?.sender?.type === SenderType.SENDER_TYPE_USER && (
            <Send msg={payload.data.message?.text?.text || ''} primary={primary} />
          )}
          {flow === 'in' && payload.data.type === EventType.EVENT_TYPE_MESSAGE && (
            <Receive
              id={ID}
              name={nick}
              msg={payload.data.message?.text?.text || ''}
              isLoading={loadingId === ID}
              avatar={avatar?.url?.url || ''}
              scale={scale.id === ID ? scale.scale : 1.0}
              primary={primary}
              onPlay={handlePlay}
            />
          )}
          {flow === 'in'
            && payload.data.type === EventType.EVENT_TYPE_ACTION
            && payload?.data?.action?.missionCompleted && (
              <Plot
                id={ID}
                cardInfo={{
                  name: payload.data.action.missionCompleted.missionName || '',
                  url: payload.data.action.missionCompleted?.missionImage.url.url || '',
                }}
                content={payload.data.action.missionCompleted.lastText}
                onExpand={onExpandMessage || (() => { })}
              />
          )}
        </div>
      ))}
      {isLoading && (
        <div
          className={styles.loading}
          key={'loading'}
          id={data.length > 0 && data[data.length - 1].isUp ? 'uploading' : 'loading'}
        >
          <Loading />
        </div>
      )}
      <div ref={lastEleRef}></div>
      <audio ref={audioRef} src={voice || ''} autoPlay={false} loop={false} />
    </div>
  );
});

export default (Messages);
